import { useState, useEffect, useRef } from 'react';
import { FileText, Download, Calendar, Tag, User, Building, CreditCard, Search, RefreshCw, ExternalLink, Upload, Trash2, CheckCircle, Clock, XCircle } from 'lucide-react';
import { clientApiService } from '../../services/clientApi';
import { Document as ApiDocument } from '../../types/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import Breadcrumb from '../../components/common/Breadcrumb';
import { useTexts } from '../../hooks/useTexts';
import { toast } from 'react-toastify';

interface Facture {
  id: number;
  numero_facture: string;
  date_paiement: string;
  montant_total: number;
  mode_paiement: string;
  statut: string;
  reference: string | null;
  souscriptions: Array<{
    projet: string;
    type_maison: string;
    montant_alloue: number;
  }>;
  token_acces: string | null;
  url_pdf: string | null;
}

export default function DocumentsPage() {
  const { getText } = useTexts();
  const [documents, setDocuments] = useState<ApiDocument[]>([]);
  const [factures, setFactures] = useState<Facture[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Charger les documents et factures au montage du composant
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Charger documents et factures en parallèle
      const [docsResponse, facturesResponse] = await Promise.all([
        clientApiService.getMyDocuments(),
        clientApiService.getMyFactures()
      ]);

      setDocuments(docsResponse.results || []);
      setFactures(facturesResponse.results || []);
    } catch (err) {
      console.error('Erreur lors du chargement des données:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    await loadData();
  };

  // Upload d'un document d'identité
  const handleUploadIdentite = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      await clientApiService.uploadDocumentIdentite(file, file.name);
      toast.success('Document uploadé avec succès. En attente de validation.');
      await loadData();
    } catch (err: any) {
      console.error('Erreur lors de l\'upload:', err);
      toast.error(err.response?.data?.error || 'Erreur lors de l\'upload du document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Suppression d'un document
  const handleDeleteDocument = async (docId: number) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;

    try {
      setDeleting(docId);
      await clientApiService.deleteDocument(docId);
      toast.success('Document supprimé avec succès');
      await loadData();
    } catch (err: any) {
      console.error('Erreur lors de la suppression:', err);
      toast.error(err.response?.data?.error || 'Erreur lors de la suppression du document');
    } finally {
      setDeleting(null);
    }
  };

  // Obtenir le badge de statut de validation
  const getValidationBadge = (statut: string) => {
    switch (statut) {
      case 'VALIDE':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-200">
            <CheckCircle className="w-3 h-3 mr-1" />
            Validé
          </span>
        );
      case 'REJETE':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 border border-red-200">
            <XCircle className="w-3 h-3 mr-1" />
            Rejeté
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
            <Clock className="w-3 h-3 mr-1" />
            En attente
          </span>
        );
    }
  };

  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'CONTRAT':
        return <FileText className="w-5 h-5 text-blue-500" />;
      case 'PLAN':
        return <Building className="w-5 h-5 text-green-500" />;
      case 'FACTURE':
        return <CreditCard className="w-5 h-5 text-orange-500" />;
      case 'RAPPORT':
        return <FileText className="w-5 h-5 text-purple-500" />;
      case 'PERMIS':
        return <FileText className="w-5 h-5 text-red-500" />;
      case 'IDENTITE':
        return <User className="w-5 h-5 text-gray-500" />;
      case 'JUSTIFICATIF':
        return <FileText className="w-5 h-5 text-yellow-500" />;
      case 'RIB':
        return <CreditCard className="w-5 h-5 text-indigo-500" />;
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'CONTRAT':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'PLAN':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'FACTURE':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'RAPPORT':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'PERMIS':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'IDENTITE':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'JUSTIFICATIF':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'RIB':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleDownload = async (doc: ApiDocument) => {
    try {
      // Créer un lien de téléchargement
      const link = document.createElement('a');
      link.href = doc.chemin_fichier;
      link.download = doc.nom_document;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Erreur lors du téléchargement:', err);
    }
  };

  const filteredDocuments = documents.filter(doc => {
    // Filtrage par type
    if (filter !== 'ALL' && doc.type_document !== filter) {
      return false;
    }
    
    // Recherche par nom ou description
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        doc.nom_document.toLowerCase().includes(searchLower) ||
        (doc.description && doc.description.toLowerCase().includes(searchLower))
      );
    }
    
    return true;
  });

  const getDocumentTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      'CONTRAT': 'Contrat',
      'PLAN': 'Plan',
      'FACTURE': 'Facture',
      'RAPPORT': 'Rapport',
      'PERMIS': 'Permis',
      'IDENTITE': 'Pièce d\'identité',
      'JUSTIFICATIF': 'Justificatif de revenus',
      'RIB': 'RIB',
      'AUTRE': 'Autre'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
            <span className="ml-3 text-gray-600">{getText('documents.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ErrorMessage error={error || 'Une erreur est survenue'} />
          <div className="mt-4 text-center">
            <button
              onClick={loadDocuments}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2 inline" />
                {getText('documents.retry')}
              </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: getText('documents.title'), current: true }
            ]}
          />
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{getText('documents.title')}</h1>
              <p className="text-gray-600 mt-2">
                {getText('documents.description')}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={loadDocuments}
                className="flex items-center px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {getText('documents.refresh')}
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{documents.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Contrats</p>
                <p className="text-2xl font-bold text-gray-900">
                  {documents.filter(d => d.type_document === 'CONTRAT').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <CreditCard className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Factures</p>
                <p className="text-2xl font-bold text-gray-900">
                  {factures.length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Autres</p>
                <p className="text-2xl font-bold text-gray-900">
                  {documents.filter(d => !['CONTRAT', 'FACTURE'].includes(d.type_document)).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Rechercher un document..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'ALL', label: 'Tous' },
                { value: 'CONTRAT', label: 'Contrats' },
                { value: 'PLAN', label: 'Plans' },
                { value: 'FACTURE', label: 'Factures' },
                { value: 'RAPPORT', label: 'Rapports' },
                { value: 'PERMIS', label: 'Permis' },
                { value: 'IDENTITE', label: 'Identité' },
                { value: 'JUSTIFICATIF', label: 'Justificatifs' },
                { value: 'RIB', label: 'RIB' },
                { value: 'AUTRE', label: 'Autres' }
              ].map((filterOption) => (
                <button
                  key={filterOption.value}
                  onClick={() => setFilter(filterOption.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === filterOption.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {filterOption.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Bouton d'upload pour Identité */}
        {filter === 'IDENTITE' && (
          <div className="mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
              <div>
                <h4 className="font-medium text-blue-900">Ajouter un document d'identité</h4>
                <p className="text-sm text-blue-700">CNI, Passeport, Permis de conduire, etc.</p>
              </div>
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleUploadIdentite}
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  id="upload-identite"
                />
                <label
                  htmlFor="upload-identite"
                  className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {uploading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Upload en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Ajouter un document
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Factures List (quand filtre = FACTURE) */}
        {filter === 'FACTURE' && (
          <div className="space-y-4">
            {factures.map((facture) => (
              <div
                key={facture.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="flex-shrink-0">
                      <CreditCard className="w-5 h-5 text-orange-500" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {facture.numero_facture}
                        </h3>

                        <span className="px-2 py-1 text-xs font-medium rounded-full border bg-green-100 text-green-800 border-green-200">
                          {facture.statut}
                        </span>

                        <span className="px-2 py-1 text-xs font-medium rounded-full border bg-blue-100 text-blue-800 border-blue-200">
                          {facture.mode_paiement}
                        </span>
                      </div>

                      <div className="text-gray-600 mb-3">
                        {facture.souscriptions.map((s, idx) => (
                          <div key={idx} className="text-sm">
                            <span className="font-medium">{s.projet}</span> - {s.type_maison}
                            <span className="text-gray-500 ml-2">({s.montant_alloue.toLocaleString('fr-FR')} FCFA)</span>
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {new Date(facture.date_paiement).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </div>

                          <div className="flex items-center font-semibold text-gray-900">
                            <CreditCard className="w-4 h-4 mr-1" />
                            {facture.montant_total.toLocaleString('fr-FR')} FCFA
                          </div>

                          {facture.reference && (
                            <div className="flex items-center">
                              <Tag className="w-4 h-4 mr-1" />
                              Réf: {facture.reference}
                            </div>
                          )}
                        </div>

                        {facture.url_pdf && (
                          <a
                            href={facture.url_pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center px-4 py-2 text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Voir la facture
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Documents List (pour les autres filtres) */}
        {filter !== 'FACTURE' && (
          <div className="space-y-4">
            {filteredDocuments.map((doc) => (
              <div
                key={doc.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="flex-shrink-0">
                      {getDocumentIcon(doc.type_document)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {doc.nom_document}
                        </h3>

                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getTypeColor(doc.type_document)}`}>
                          {getDocumentTypeLabel(doc.type_document)}
                        </span>

                        {(doc as any).statut_validation && getValidationBadge((doc as any).statut_validation)}

                        {doc.signature_requise && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 border border-red-200">
                            Signature requise
                          </span>
                        )}
                      </div>

                      {(doc as any).motif_rejet && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-3">
                          <p className="text-sm text-red-700">
                            <strong>Motif du rejet :</strong> {(doc as any).motif_rejet}
                          </p>
                        </div>
                      )}

                      {doc.description && (
                        <p className="text-gray-600 mb-3 leading-relaxed">
                          {doc.description}
                        </p>
                      )}

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {new Date(doc.date_upload).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </div>

                          {doc.taille_fichier_mb && (
                            <div className="flex items-center">
                              <FileText className="w-4 h-4 mr-1" />
                              {doc.taille_fichier_mb.toFixed(1)} MB
                            </div>
                          )}

                          {doc.extension_fichier && (
                            <div className="flex items-center">
                              <Tag className="w-4 h-4 mr-1" />
                              {doc.extension_fichier.toUpperCase()}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleDownload(doc)}
                            className="flex items-center px-4 py-2 text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Télécharger
                          </button>

                          {/* Bouton de suppression (uniquement pour les documents non validés) */}
                          {(doc as any).peut_supprimer && (
                            <button
                              onClick={() => handleDeleteDocument(doc.id)}
                              disabled={deleting === doc.id}
                              className={`flex items-center px-4 py-2 text-red-600 bg-red-100 rounded-lg hover:bg-red-200 transition-colors ${deleting === doc.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {deleting === doc.id ? (
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4 mr-2" />
                              )}
                              Supprimer
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {((filter === 'FACTURE' && factures.length === 0) || (filter !== 'FACTURE' && filteredDocuments.length === 0)) && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Aucun document trouvé
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || filter !== 'ALL'
                ? 'Aucun document ne correspond à votre recherche'
                : 'Vous n\'avez pas encore de documents'
              }
            </p>
            {(searchTerm || filter !== 'ALL') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilter('ALL');
                }}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Voir tous les documents
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
